import { IsStringEmpty, IsVarEmpty, isStringNotEmpty, isVarNotEmpty } from '../utils/GlobalFunctions'
import { URLConstructor } from '../utils/URLBuilder'
import { AkixiEntities } from './AkixiEntities'
import { TelSysTypes } from './TelephonyServerTypes'
import { COMMA, DASH, EMPTY_STRING, FALSE, TRUE } from './Common'
import { ADVANCED_FIELDS } from './TelSysConstants'
import { SLASH } from './Separators'
import { ANY_VALUE_STRING_NUMERIC, ANY_DEVICE_VALUE } from '../common/DefaultValues'
import { ArrayUtils } from '../utils/ArrayUtils'
import { CRMConfigurationTemplateType } from '../../ReactRouting/Components/Organisms/CRMConfiguration/Modal/Constants/Constants'

/** Primitive query parameter value type used in admin list and filter URLs. */
type QueryParamPrimitive = string | number | boolean

type CallDetailsInfo = {
    fallbackID?: string
    itemID?: string
    itemIDNum?: number
    start?: string
    end?: string
}

export const CACHE_TIME: number = 10000
export const NO_CACHE_TIME: number = 0
export const EXTENDED_CACHE_TIME: number = 500000

// URL elements.
export enum APIElements {
    CCS = 'CCS',
    API = 'API',
    APP_V2 = 'App/v2',
    API_VERSION = 'v1',
    API_INTERNAL = 'internal',
    API_ADMIN = 'admin',
    API_INTEGRATION = 'integration',
    API_APP = 'App',
    AKIXI_2019 = 'Akixi2019'
}

// Path params.
export enum APIPathParams {
    ACTION = 'action',
    RESULT = 'result',
    CURRENT_USER = 'currentUser',
    STATUS = 'status',
    DETAIL = 'detail',
    INFO = 'info',
    MORE_INFORMATION = 'more-information',
    CALL_RECORDING_INTEGRATIONS = 'callRecordingIntegration',
    CONFIG = 'config',
    MAKE_CALL = 'makecall',
    TRANSFER_ONE_STEP = 'transferonestep',
    DIVERT_DEFLECT = 'divertdeflectconnection',
    INQUIRY_TRANSFER_TO = 'makeconsultationcall',
    PICKUP_CONNECTION = 'pickupconnection',
    SET_DND = 'setdnd',
    COMPLETE_TRANSFER = 'transfercomplete',
    CME = 'cme',
    SESSION = 'session',
    TEST_CONNECTION = 'testConnection',
    FEATURE = 'feature',
    INSIGHTS = 'insights',
    REPORT = 'report',
    SET_ACC_CODE = 'setacccode',
    FINISH = 'finish',
    TRACKER = 'tracker',
    INVITE = 'invite',
    SETTINGS = 'settings',
    NOTIFICATION_PUSH_TOKEN = 'notificationPushToken',
    BRANDING = 'branding',
    LIST = 'list',
    USERNAME = 'username',
    DEFAULT_REPORT = 'defaultReport',
    REPORT_SET = 'defaultReportSet',
    MOVE_TO_BIN = 'moveToBin',
    BROADWORKS = 'broadworks',
    AUTHENTICATE = 'authenticate',
    AUTH = 'auth',
    ID_ON_TEL_SYS = 'idOnTelSys',
    ID_ON_TEL_SYS_OWNER = 'idOnTelSysOwner',
    SIGNUP = 'signUp',
    UTILITIES = 'utilities',
    TIMEZONE = 'timezone',
    DEFAULT = 'default',
    LICENCE = 'license',
    OAUTH = 'oauth',
    INTEGRATION = 'integration',
    CALL_CONTROL_OPTIONS = 'callControlOptions',
    SEND_EMAIL = 'sendEmail',
    LICENCES = 'licenses',
    CHANGE_PASSWORD = 'changePassword',
    RESET_PASSWORD = 'resetPassword',
    PASSWORD = 'password',
    GENERATE = 'generate',
    STRENGTH = 'strength',
    CALL_CAPTURE = 'callCapture',
    HIDE_INSIGHTS_HUB = 'hideInsightsHub',
    SHOW_INSIGHTS_HUB = 'showInsightsHub',
    WEBSOCKET = 'websocket',
    PRESENCE = 'presence',
    FILTER_OPTION = 'filterOption',
    DEVICE_TYPES = 'deviceTypes',
    LOCALES = 'locales',
    FIRST_SYNC_STATUS = 'firstSyncStatus',
    API_KEYS = 'apiKeys',
    SITE_SURVEY = 'siteSurvey',
    USER = 'user',
    USERS = 'users',
    ROUTE = 'route',
    AUTH_OUTFLOW = 'authOutflow',
    ACCEPT_TERMS_OF_SERVICE = 'acceptTermsOfService',
    REJECT_TERMS_OF_SERVICE = 'rejectTermsOfService',
    LOGIN_LANDING = 'loginLanding',
    SERVICES = 'services',
    LANGUAGE = 'language',
    LOGIN = 'login',
    RECAPTCHA = 'recaptcha',
    FORGOT_CREDENTIALS = 'forgotCredential',
    FAVOURITES = 'favourites',
    DARK_MODE = 'darkmode',
    MFA_SUCCESS = 'mfaSuccess',
    USER_AGENTS = 'userAgents',
    USER_AGENT = 'userAgent',
    BRANDING_CONFIG = 'brandingConfig',
    TOKEN = 'token',
    TOKEN_NO_REFRESH = 'tokenNoRefresh',
    REFRESH = 'refresh',
    VIDEO = 'video',
    REFERENCES = 'references',
    ENTITY_TYPES = 'entityTypes',
    INTEGRATOR_CONFIG = 'integratorClient',
    CRM_CONFIGURATION = 'crm',
    CONFIGS = 'configs',
    TYPES = 'types',
    TEMPLATE = 'template',
    LOGGING = 'logging',
    MESSAGES = 'messages',
    DATABASE = 'database',
    RELOAD = 'reload',
    ADMIN_CONTROL = 'adminControl',
    SECURITY_GROUPS = 'securityGroups',
    TEAMS = 'teams',
    LANGUAGES = 'languages',
    /** CME-scoped directory entry list path segment (e.g. {@code /cme/{id}/directory}). */
    CME_DIRECTORY = 'directory',
    CHANGE_LOG = 'changeLog',
    COPY_REPORTS = 'copy-reports',
    COPY_REPORTS_REPORTS = 'reports',
    COPY_REPORTS_TARGETS = 'targets',
    COPY_REPORTS_EXECUTE = 'execute',
    SIGN_IN_AS = 'signInAs',
    MODULE_STATUS = 'moduleStatus',
    LICENCE_MANAGEMENT = 'licenceManagement',
    POLICIES = 'policies',
    COMPLIANCE_RECORDING_POLICIES = 'complianceRecordingPolicies',
    ASYNC_RESULT = 'asyncResult',
    BATCH_ASYNC_RESULT = 'asyncBatchResult',
    TENANT = 'tenant',
    VOICE_APPS = 'voiceApps',
    INCOMPATIBILITIES = 'incompatibilities',
    DOWNLOAD = 'download',
    START = 'start'
}

enum DNDActions {
    DND_ON = 'on',
    DND_OFF = 'off'
}

// Query params.
export enum QueryParams {
    DEVICE_TYPE_FILTER = 'deviceTypeFilter',
    FILTER_DEVICE_RANGE = 'filterDeviceRange',
    FILTER_AGENT_RANGE = 'filterAgentRange',
    GROUP_TYPE_FILTER = 'groupTypeFilter',
    CODE_TYPE_FILTER = 'codeTypeFilter',
    CODE_SCOPE_FILTER = 'codeScopeFilter',
    CALL_REC_INT_TYPE_FILTER = 'callRecIntTypeFilter',
    CALL_REC_INT_SCOPE_FILTER = 'callRecIntScopeFilter',
    SORT = 'sort',
    RECURSIVE = 'recursive',
    TEL_SYS_FIELDS = 'telSysFields',
    ADVANCED_FIELDS_PARAM = 'advancedFields',
    TEL_SYS_ID = 'telSysID',
    PARTITION_FIELDS = 'partitionFields',
    AUTO_DELETE_USERS = 'autoDeleteUsers',
    DEVICE_FIELDS = 'deviceFields',
    AGENT_FIELDS = 'agentFields',
    CODE = 'code',
    FILTER = 'filter',
    FILTER_BY_PARTITION_LICENCE = 'filterByPartitionLicence',
    FILTER_BY_PARTITION_SYNC_TYPE = 'filterByPartitionSyncType',
    FILTER_BY_PARTITION_STATUS = 'filterByPartitionStatus',
    DEFAULT_BRANDING_STYLE = 'defaultBrandingStyle',
    DEFAULT_REPORT_TYPE = 'defaultReportType',
    DEFAULT_REPORT_NAME = 'defaultReportName',
    MOVE_TO_BIN = 'moveToBin',
    MONITORED_ONLY = 'monitoredOnly',
    LICENCE_SETTING_FIELDS = 'licenseNames',
    SEARCH_QUERY = 'searchQuery',
    CRM_TYPE = 'crmType',
    TYPE = 'type',
    IP_ADDRESS_OR_HOSTNAME = 'ipAddressOrHostname',
    IANA = 'IANA',
    BRANDING_ID = 'brandingId',
    CME_FIELDS = 'CMEnvironmentFields',
    PARENT_CME_ID = 'parentCMEId',
    OFFSET = 'offset',
    LIMIT = 'limit',
    INTEGRATOR_CLIENT_FIELDS = 'IntegratorClientFields',
    CRM_CONFIGURATION_FIELDS = 'crmConfigurationFields',
    CRM_CONFIGURATION_TEMPLATE_TYPE = 'configurationType',
    CME_ID = 'CMEnvironmentID',
    USER_FIELDS = 'userFields',
    SCOPE_GROUP_HIERARCHY = 'scopeGroupHierarchy',
    CODE_FIELDS = 'codeFields',
    CALL_REC_INT_FIELDS = 'callRecIntFields',
    PARTITION_ID_IN_TELSYS = 'partitionIDInTelSys',
    USER_TYPE_FILTER = 'userTypeFilter',
    LICENCE_FILTER = 'licenceFilter',
    ACTIVITY_FILTER = 'activityFilter',
    LOCKED_OUT_FILTER = 'lockedOutFilter',
    PENDING_DELETION_FILTER = 'pendingDeletionFilter',
    PERMANENT_DELETION = 'permanentDeletion',
    CXI_TELSYS_ONLY = 'CXITelSysOnly'
}

// MFA path params.
enum MFAPathParams {
    MFA = 'mfa',
    SECRET_CODE = 'secretCode',
    SETUP = 'mfaSetup',
    ENABLE = 'enableMfa',
    OTP = 'otp'
}

// Headers.
export const INVOKE_ID_HEADER: string = 'X-InvokeID'

// Actions.

// Connection control.
export enum ConnectionControls {
    ANSWER_CALL = 'answercall',
    CLEAR_CONNECTION = 'clearconnection',
    CLEAR_CALL_MODEL = 'clearcallmodel'
}

// Sign in/out.
export enum SignInOut {
    SIGN_IN = 'signin',
    SIGN_OUT = 'signout'
}

// Status updates.
export enum ACDStatusUpdates {
    AVAILABLE = 'setavailable',
    WRAP_UP = 'setwrapup',
    DND = 'setacddnd',
    NOT_AVAILABLE = 'setnotavailable'
}

// Monitoring actions.
export enum MonitoringActions {
    MONITOR_CONNECTION = 'monitorconnection',
    RECORD_CALL = 'recordcall',
    REGISTER_CALLBACK = 'registercallback'
}

// Sorting.
export enum SortingParameters {
    DESCRIPTION_ASC = 'Description:ASC'
}

export enum EntityFields {
    COMMS_STATUS = 'CommsStatus'
}

export enum LogInOut {
    LOG_IN = 'login',
    LOG_OUT = 'logout'
}

export enum SSOLogIn {
    SSO = 'sso'
}

export enum TelSysFields {
    DESCRIPTION = 'Description',
    PARTITION_LICENCES = 'PartitionLicences',
    TELSYS_TYPE = 'TelSysType',
    LICENSED_FOR_RPT_USER_TYPE = 'LicensedForRptUserType',
    CALL_REC_INT_ID_DEFAULT = 'CallRecIntIDDefault',
    E164_ENABLED = 'E164Enabled',
    INTERNAL_DIGIT_LEN = 'InternalDigitLen',
    COUNTRY_CODE = 'CountryCode',
    TIME_ZONE = 'TimeZone',
    START_OF_DAY = 'StartOfDay',
    PARTITIONS_RPTING_SCOPE_ALL_ENABLED = 'PartitionsRptingScopeAllEnabled'
}

/**
 * Field names for {@link telSysListURL} {@code telsysFields} to mirror legacy admin TelSys list columns.
 */
export const TELSYS_ADMIN_LIST_FIELDS: readonly string[] = [
    'Description',
    'ListTrialDisplay',
    'ListTelSysTypeDisplay',
    'ListLicenseDisplay',
    'ListExtensionCountDisplay',
    'ListAcdAgentCountDisplay',
    'ListAppUserCountDisplay',
    'ListPartitionCountDisplay',
    'ListModuleStatusDisplay'
]

/**
 * Field names for code list API (admin list parity; no UniqueID in table).
 */
export const CODE_ADMIN_LIST_FIELDS: readonly string[] = [
    'Description',
    'Code',
    'CodeType',
    'TelSysID',
    'TelSysDescription',
    'PartIDInTelSys',
    'PartitionDescription',
    'PartitionGUID',
    'CodeGUID'
]

/**
 * Field names for recording integration list API (admin list parity).
 */
export const RECORDING_ADMIN_LIST_FIELDS: readonly string[] = [
    'Description',
    'CallRecIntType',
    'CallRecIntTypeDescription',
    'TelSysID',
    'TelSysDescription',
    'PartIDInTelSys',
    'PartitionDescription',
    'CallRecIntGUID'
]

/**
 * Field names for {@link usersAdminListURL} {@code userFields} for the routed application users list.
 */
export const USER_ADMIN_LIST_FIELDS: readonly string[] = [
    'Username',
    'FullName',
    'LockOut',
    'PendingDeletion',
    'ListActivity',
    'ListLastLogon',
    'ListActiveSignIns',
    'ListPermissionsSummary',
    'ListTelSysDescription'
]

/**
 * Field names for partition list API (admin list parity).
 */
export const PARTITION_ADMIN_LIST_FIELDS: readonly string[] = [
    'PartitionGUID',
    'Description',
    'PartIDInTelSys',
    'IsGlobal',
    'LicensedForRptUserType',
    'ExtCount',
    'UserCount',
    'AcdAgentCount',
    'SyncType',
    'CommsStatus'
]

/**
 * Field names for paginated admin device list ({@code deviceFields}) for routed list parity.
 * {@code DeviceType} is the display string returned by the WS device list; {@code ListDeviceTypeDisplay} is not a WS field.
 * Include {@code QueueStatus} so MS Teams (and other) queue rows return status for the device list queue column.
 */
export const DEVICE_ADMIN_LIST_FIELDS: readonly string[] = [
    'DeviceNumber',
    'DeviceNumberInternal',
    'Description',
    'PartitionDescription',
    'PartIDInTelSys',
    'DeviceType',
    'DeviceTypeID',
    'IDOnTelSys',
    'QueueStatus',
    'Users'
]

export enum CMEFields {
    CAPABILITIES = 'Capabilities'
}

// Filter joiner function.
const filterJoiner = (filters?: string[]): Definable<string> => filters?.join(DASH)

// Router base path.
export const getAppV2APIBasePath = (): URLConstructor =>
    URLConstructor.newInstance({ pathParams: [APIElements.CCS, APIElements.APP_V2] })

// API base.
export const getAPIBasePath = (): URLConstructor =>
    URLConstructor.newInstance({ pathParams: [APIElements.CCS, APIElements.API, APIElements.API_VERSION] })

// Base path.
export const getAPIInternalBaseURL = (): URLConstructor => getAPIBasePath().appendPathParams(APIElements.API_INTERNAL)

export const serverStatusURL: URLConstructor = getAPIInternalBaseURL().appendPathParams(APIPathParams.STATUS)

// Integration path.
export const getAPIIntegrationBaseURL = (): URLConstructor =>
    getAPIBasePath().appendPathParams(APIElements.API_INTEGRATION)

// Base Servlet Path.
export const getBaseServletPath = (urlMapping?: string): URLConstructor =>
    URLConstructor.newInstance({
        pathParams: [APIElements.CCS, APIElements.API_APP, urlMapping ?? APIElements.AKIXI_2019]
    })

// Admin Base path.
export const getAPIAdminBasePath = (): URLConstructor => getAPIBasePath().appendPathParams(APIElements.API_ADMIN)

export const licenceManagementListURL = (
    telSysID: string | number,
    partitionIDInTelSys: string | number,
    cmeID: string,
    recursive?: boolean,
    offset?: number,
    limit?: number,
    sort?: string,
    search?: string
): URLConstructor => {
    const params: Record<string, string | number | boolean | undefined> = {
        [QueryParams.RECURSIVE]: recursive ?? true,
        [QueryParams.OFFSET]: offset,
        [QueryParams.LIMIT]: limit,
        [QueryParams.SORT]: sort,
        [QueryParams.SEARCH_QUERY]: search
    }

    if (isStringNotEmpty(cmeID)) {
        params[QueryParams.CME_ID] = cmeID
    }

    return getAPIAdminBasePath()
        .appendPathParams(
            APIPathParams.LICENCE_MANAGEMENT,
            AkixiEntities.TEL_SYS,
            telSysID,
            AkixiEntities.PARTITION,
            partitionIDInTelSys
        )
        .appendQueryParams(params)
}

/**
 * Query parameters for {@link changeLogListURL} / {@link getChangeLogList} (admin audit trail list).
 */
export type ChangeLogListQueryParams = {
    readonly userID?: string
    readonly since?: string
    /** Upper bound; use local {@code YYYY-MM-DD HH:mm:ss} for WS {@code CReqRespChangeLogList} date parsing. */
    readonly until?: string
    readonly include?: string
    readonly ignore?: string
    readonly type?: string
    readonly CMEnvironmentID?: string
    readonly RecursiveCMEnvironmentMatching?: boolean
    readonly telSysIDs?: string
    readonly partitionIDs?: string
    readonly primaryID?: string
    readonly secondaryID?: string
    readonly deNull?: boolean
    readonly pageSize?: number
    readonly sort?: string
    readonly pageChangeFromKey?: string
    readonly pageChangeDirection?: string
}

/**
 * Builds the GET URL for {@code /v1/admin/changeLog}.
 *
 * @param params - Filter, pagination, and sort query parameters.
 */
export const changeLogListURL = (params: ChangeLogListQueryParams): URLConstructor => {
    const query: Record<string, Definable<QueryParamPrimitive>> = {
        userID: isStringNotEmpty(params.userID) ? params.userID : undefined,
        since: isStringNotEmpty(params.since) ? params.since : undefined,
        until: isStringNotEmpty(params.until) ? params.until : undefined,
        include: isStringNotEmpty(params.include) ? params.include : undefined,
        ignore: isStringNotEmpty(params.ignore) ? params.ignore : undefined,
        type: isStringNotEmpty(params.type) ? params.type : undefined,
        CMEnvironmentID: isStringNotEmpty(params.CMEnvironmentID) ? params.CMEnvironmentID : undefined,
        RecursiveCMEnvironmentMatching: params.RecursiveCMEnvironmentMatching === false ? 'false' : undefined,
        telSysIDs: isStringNotEmpty(params.telSysIDs) ? params.telSysIDs : undefined,
        partitionIDs: isStringNotEmpty(params.partitionIDs) ? params.partitionIDs : undefined,
        primaryID: isStringNotEmpty(params.primaryID) ? params.primaryID : undefined,
        secondaryID: isStringNotEmpty(params.secondaryID) ? params.secondaryID : undefined,
        deNull: params.deNull === true ? true : undefined,
        pageSize: isVarNotEmpty(params.pageSize) ? params.pageSize : undefined,
        sort: isStringNotEmpty(params.sort) ? params.sort : undefined,
        pageChangeFromKey: isStringNotEmpty(params.pageChangeFromKey) ? params.pageChangeFromKey : undefined,
        pageChangeDirection: isStringNotEmpty(params.pageChangeDirection) ? params.pageChangeDirection : undefined
    }

    return getAPIAdminBasePath().appendPathParams(APIPathParams.CHANGE_LOG).appendQueryParams(query)
}

/**
 * Shared CME / TelSys scope query for App v2 log management REST calls.
 */
export type LogManagementScopeQueryParams = {
    readonly cmeId: string
    /**
     * When {@code false}, sends {@code RecursiveCMEnvironmentMatching=false}. When {@code true} or omitted, the
     * parameter is omitted so the server treats recursive matching as enabled (legacy default).
     */
    readonly recursive?: boolean
    readonly telSysId: string
}

const logManagementScopeQueryRecord = (params: LogManagementScopeQueryParams): Record<string, string | boolean> => {
    const query: Record<string, string | boolean> = {
        CMEnvironmentID: params.cmeId,
        telSysID: params.telSysId
    }

    if (params.recursive === false) query.RecursiveCMEnvironmentMatching = 'false'

    return query
}

/**
 * Query parameters for {@link logManagementStatusURL}.
 */
export type LogManagementStatusQueryParams = LogManagementScopeQueryParams & {
    readonly offset: number
    readonly limit: number
    readonly searchQuery?: string
}

/**
 * Builds {@code GET /v1/admin/logging/status} for the routed log management list.
 *
 * @param params - CME / TelSys scope, pagination, and optional search text.
 */
export const logManagementStatusURL = (params: LogManagementStatusQueryParams): URLConstructor => {
    const query: Record<string, Definable<string | number | boolean>> = {
        ...logManagementScopeQueryRecord(params),
        offset: params.offset,
        limit: params.limit
    }

    if (isStringNotEmpty(params.searchQuery)) {
        query.searchQuery = params.searchQuery
    }

    return getAPIAdminBasePath().appendPathParams(APIPathParams.LOGGING, APIPathParams.STATUS).appendQueryParams(query)
}

/**
 * Builds {@code POST /v1/admin/logging/start|stop|reset} with scope query parameters.
 *
 * @param action - Mutation segment after {@code /v1/admin/logging/}.
 * @param scope - CME / TelSys scope forwarded as query parameters.
 */
export const logManagementMutationURL = (
    action: 'start' | 'stop' | 'reset',
    scope: LogManagementScopeQueryParams
): URLConstructor =>
    getAPIAdminBasePath()
        .appendPathParams(APIPathParams.LOGGING, action)
        .appendQueryParams(logManagementScopeQueryRecord(scope))

/**
 * Builds {@code GET /v1/admin/logging} with repeated {@code fileName} keys for multi-file download.
 *
 * @param fileNames - Selected log file names.
 */
export const logManagementDownloadURL = (fileNames: readonly string[]): URLConstructor =>
    getAPIAdminBasePath()
        .appendPathParams(APIPathParams.LOGGING)
        .appendQueryParams({
            fileName: [...fileNames]
        })

/**
 * Builds {@code GET/PUT /v1/admin/logging/settings}.
 */
export const loggingSettingsURL = (): URLConstructor =>
    getAPIAdminBasePath().appendPathParams(APIPathParams.LOGGING, APIPathParams.SETTINGS)

/**
 * Query parameters for {@link telModuleStatusURL}.
 */
export type TelModuleStatusQueryParams = {
    readonly cmeId: string
    /**
     * When {@code false}, sends {@code RecursiveCMEnvironmentMatching=false}. When {@code true} or omitted, the
     * parameter is omitted so the server treats recursive matching as enabled (legacy default).
     */
    readonly recursive?: boolean
    readonly telSysId: string
    readonly offset: number
    readonly limit: number
    readonly searchQuery?: string
}

/**
 * Builds {@code GET /v1/admin/telsys/moduleStatus} for the routed telephone module status list.
 *
 * @param params - CME / TelSys scope, pagination, and optional search text.
 */
export const telModuleStatusURL = (params: TelModuleStatusQueryParams): URLConstructor => {
    const query: Record<string, Definable<string | number | boolean>> = {
        CMEnvironmentID: params.cmeId,
        telSysID: params.telSysId,
        offset: params.offset,
        limit: params.limit
    }

    if (params.recursive === false) {
        query.RecursiveCMEnvironmentMatching = 'false'
    }

    if (isStringNotEmpty(params.searchQuery)) {
        query.searchQuery = params.searchQuery
    }

    return getAPIAdminBasePath()
        .appendPathParams(AkixiEntities.TEL_SYS, APIPathParams.MODULE_STATUS)
        .appendQueryParams(query)
}

// User info endpoint.
export const userInfoURL: URLConstructor = getAPIBasePath().appendPathParams(APIPathParams.CURRENT_USER)

// Session API.
export const sessionURL: URLConstructor = getAPIBasePath().appendPathParams(APIPathParams.SESSION)

// Telsys API internal.
export const telSysInternalURL: URLConstructor = getAPIInternalBaseURL().appendPathParams(AkixiEntities.TEL_SYS)

// Entity creator URL.
export const telSysCreatorURL: URLConstructor = getAPIAdminBasePath().appendPathParams(AkixiEntities.TEL_SYS)
export const telServerWithCommStatusURL: URLConstructor = telSysCreatorURL.appendQueryParams({
    [QueryParams.TEL_SYS_FIELDS]: EntityFields.COMMS_STATUS
})
export const partitionCreatorURL = (telSysID: number | string): URLConstructor =>
    getAPIAdminBasePath().appendPathParams(AkixiEntities.TEL_SYS, telSysID, AkixiEntities.PARTITION)
export const partitionsWithCommStatusURL = (telSysID: number | string): URLConstructor =>
    partitionCreatorURL(telSysID).appendQueryParams({
        [QueryParams.PARTITION_FIELDS]: EntityFields.COMMS_STATUS
    })

// Entity update URL.
export const telSysUpdateURL = (telSysID: number | string): URLConstructor =>
    telSysCreatorURL.appendPathParams(telSysID)
export const telSysByIDInteralURL = (telSysID: number | string): URLConstructor =>
    telSysInternalURL.appendPathParams(telSysID)
export const telSysFirstSyncURL = (telSysID: number | string): URLConstructor =>
    telSysInternalURL.appendPathParams(telSysID).appendPathParams(APIPathParams.FIRST_SYNC_STATUS)
export const partitionUpdateURL = (telSysID: number | string, partitionID: string | number): URLConstructor =>
    partitionCreatorURL(telSysID).appendPathParams(partitionID)

// Base URL creators.
export const partitionsBaseURL = (telSysID: string | number): URLConstructor =>
    getAPIInternalBaseURL().appendPathParams(AkixiEntities.TEL_SYS, telSysID, AkixiEntities.PARTITION)
export const partitionBaseURL = (telSysID: string | number, partitionID: string | number): URLConstructor =>
    getAPIInternalBaseURL().appendPathParams(AkixiEntities.TEL_SYS, telSysID, AkixiEntities.PARTITION, partitionID)
export const teamsTelSysBaseURL = (telSysID: string | number): URLConstructor =>
    getAPIInternalBaseURL().appendPathParams(APIPathParams.TEAMS, AkixiEntities.TEL_SYS, telSysID)
export const teamsPartitionBaseURL = (telSysID: string | number, partition: string | number): URLConstructor =>
    teamsTelSysBaseURL(telSysID).appendPathParams(AkixiEntities.PARTITION, partition)

export const partitionAdminBaseURL = (telSysID: string | number): URLConstructor =>
    getAPIAdminBasePath().appendPathParams(AkixiEntities.TEL_SYS, telSysID, AkixiEntities.PARTITION)
const deviceBaseURL = (telSysID: string | number, partitionID: string | number, deviceID?: string): URLConstructor =>
    partitionBaseURL(telSysID, partitionID).appendPathParams(AkixiEntities.DEVICE, deviceID ?? EMPTY_STRING)
const deviceAdminBaseURL = (telSysID: string, partitionID: string, deviceID?: string): URLConstructor =>
    partitionAdminBaseURL(telSysID).appendPathParams(partitionID, AkixiEntities.DEVICE, deviceID ?? EMPTY_STRING)
const agentBaseURL = (telSysID: string | number, partitionID: string | number, agentID?: string): URLConstructor =>
    partitionBaseURL(telSysID, partitionID).appendPathParams(AkixiEntities.ACD_AGENTS, agentID ?? EMPTY_STRING)
const recordingBaseURL = (telSysID: string, telSysCallID: string, recordingID?: string): URLConstructor =>
    getAPIInternalBaseURL().appendPathParams(
        AkixiEntities.TEL_SYS,
        telSysID,
        AkixiEntities.CALL,
        telSysCallID,
        AkixiEntities.RECORDING,
        recordingID ?? EMPTY_STRING
    )

// Get API URLs.
export const devicesURL = (
    telSysID: string | number,
    partitionID: string | number,
    monitoredOnly: boolean,
    filters?: string[],
    deviceFields?: string[],
    searchQuery?: string,
    sendFiltersWithComma?: boolean,
    limit?: number,
    offset?: number,
    sort?: string
): URLConstructor =>
    partitionBaseURL(telSysID, partitionID)
        .appendPathParams(AkixiEntities.DEVICE)
        .appendQueryParams({
            [QueryParams.DEVICE_TYPE_FILTER]: sendFiltersWithComma ? filters?.join(COMMA) : filterJoiner(filters),
            [QueryParams.DEVICE_FIELDS]: deviceFields?.join(COMMA),
            [QueryParams.MONITORED_ONLY]: monitoredOnly ? 'true' : 'false',
            ...(isVarNotEmpty(limit) ? { [QueryParams.LIMIT]: limit } : {}),
            ...(isVarNotEmpty(offset) ? { [QueryParams.OFFSET]: offset } : {}),
            ...(isStringNotEmpty(searchQuery) ? { [QueryParams.SEARCH_QUERY]: searchQuery } : {}),
            ...(isStringNotEmpty(sort) ? { [QueryParams.SORT]: sort } : {})
        })

/**
 * Builds the paginated device list URL for a TelSys / partition scope (admin API, SOAP-backed list).
 *
 * @param telSysID - Telephone system ID path segment.
 * @param partitionID - Partition ID within the telephone system.
 * @param monitoredOnly - When true, exclude devices with override monitoring.
 * @param filters - Optional device type filter ranges (joined with {@link filterJoiner} unless {@code sendFiltersWithComma}).
 * @param deviceFields - Optional device field names per row.
 * @param searchQuery - Optional search query.
 * @param sendFiltersWithComma - When true, join {@code filters} with commas instead of dashes.
 * @param limit - Maximum rows to return (paired with {@code offset}).
 * @param offset - Zero-based offset.
 * @param sort - Optional sort expression (e.g. {@code DeviceNumber:ASC}, {@code DeviceTypeID:ASC}).
 * @returns URL builder for {@code GET .../admin/telSys/{id}/partition/{pid}/device}.
 */
export const devicesAdminListURL = (
    telSysID: string | number,
    partitionID: string | number,
    monitoredOnly: boolean,
    filters?: string[],
    deviceFields?: string[],
    searchQuery?: string,
    sendFiltersWithComma?: boolean,
    limit?: number,
    offset?: number,
    sort?: string,
    filterDeviceRange?: string
): URLConstructor => // NOSONAR
    partitionAdminBaseURL(telSysID)
        .appendPathParams(partitionID, AkixiEntities.DEVICE)
        .appendQueryParams({
            [QueryParams.DEVICE_TYPE_FILTER]: sendFiltersWithComma ? filters?.join(COMMA) : filterJoiner(filters),
            [QueryParams.DEVICE_FIELDS]: deviceFields?.join(COMMA),
            [QueryParams.MONITORED_ONLY]: monitoredOnly ? 'true' : 'false',
            ...(isVarNotEmpty(limit) ? { [QueryParams.LIMIT]: limit } : {}),
            ...(isVarNotEmpty(offset) ? { [QueryParams.OFFSET]: offset } : {}),
            ...(isStringNotEmpty(searchQuery) ? { [QueryParams.SEARCH_QUERY]: searchQuery } : {}),
            ...(isStringNotEmpty(sort) ? { [QueryParams.SORT]: sort } : {}),
            ...(isStringNotEmpty(filterDeviceRange) ? { [QueryParams.FILTER_DEVICE_RANGE]: filterDeviceRange } : {})
        })

export const deviceURL = (telSysID: string, partitionID: string, deviceID: string): URLConstructor =>
    deviceAdminBaseURL(telSysID, partitionID, deviceID)
export const accountCodesURL = (telSysID: string, paritionID: string, filters?: string[]): URLConstructor =>
    partitionBaseURL(telSysID, paritionID)
        .appendPathParams(AkixiEntities.CODES)
        .appendQueryParams({ [QueryParams.CODE_TYPE_FILTER]: filterJoiner(filters) })
export const groupsURL = (telSysID: string, partitionID: string, agentID: string, filters?: string[]): URLConstructor =>
    agentBaseURL(telSysID, partitionID, agentID)
        .appendPathParams(AkixiEntities.GROUPS)
        .appendQueryParams({ [QueryParams.GROUP_TYPE_FILTER]: filterJoiner(filters) })

/**
 * Internal API: group devices a given device number is a member of ({@code .../device/{num}/group}).
 */
export const deviceGroupMembershipURL = (
    telSysID: string | number,
    partitionID: string | number,
    deviceNumber: string,
    filters?: string[]
): URLConstructor =>
    deviceBaseURL(String(telSysID), String(partitionID), deviceNumber)
        .appendPathParams(AkixiEntities.GROUPS)
        .appendQueryParams({ [QueryParams.GROUP_TYPE_FILTER]: filterJoiner(filters) })

/**
 * Internal API: user IDs linked to a device ({@code .../device/{num}/linkedusers}).
 */
export const deviceLinkedUsersURL = (
    telSysID: string | number,
    partitionID: string | number,
    deviceNumber: string
): URLConstructor =>
    deviceBaseURL(String(telSysID), String(partitionID), deviceNumber).appendPathParams(AkixiEntities.LINKED_USERS)

/**
 * Internal API: user IDs linked to an ACD agent ({@code .../acdagent/{id}/linkedusers}).
 */
export const agentLinkedUsersURL = (
    telSysID: string | number,
    partitionID: string | number,
    agentID: string
): URLConstructor => agentBaseURL(telSysID, partitionID, agentID).appendPathParams(AkixiEntities.LINKED_USERS)
export const codesURL = (telSysID: string, paritionID: string, agentID: string, filters?: string[]): URLConstructor =>
    agentBaseURL(telSysID, paritionID, agentID)
        .appendPathParams(AkixiEntities.CODES)
        .appendQueryParams({ [QueryParams.CODE_TYPE_FILTER]: filterJoiner(filters) })
/**
 * Field names for paginated internal ACD agent list ({@code ACDAgentFields}) for admin list parity.
 */
export const AGENT_ADMIN_LIST_FIELDS: readonly string[] = [
    'PartIDInTelSys',
    'OverrideMonitoring',
    'ListMonitoredDisplay',
    'Description',
    'AgentNumberInternal',
    'ListPartitionNameDisplay',
    'ListOmniChannelCategoryTypeDisplay',
    'AgentGUID',
    'IDOnTelSys'
]

export const agentsURL = (
    telSysID: string | number,
    partInTelSys: string | number,
    monitoredOnly: boolean,
    agentFields?: string[],
    searchQuery?: string,
    offset?: number,
    limit?: number,
    sort?: string,
    filterAgentRange?: string
): URLConstructor =>
    partitionBaseURL(telSysID, partInTelSys)
        .appendPathParams(AkixiEntities.ACD_AGENTS)
        .appendQueryParams({
            [QueryParams.AGENT_FIELDS]: agentFields?.join(COMMA),
            [QueryParams.MONITORED_ONLY]: monitoredOnly ? 'true' : 'false',
            ...(isStringNotEmpty(searchQuery) ? { [QueryParams.SEARCH_QUERY]: searchQuery } : {}),
            ...(isVarNotEmpty(offset) ? { [QueryParams.OFFSET]: offset } : {}),
            ...(isVarNotEmpty(limit) ? { [QueryParams.LIMIT]: limit } : {}),
            ...(isStringNotEmpty(sort) ? { [QueryParams.SORT]: sort } : {}),
            ...(isStringNotEmpty(filterAgentRange) ? { [QueryParams.FILTER_AGENT_RANGE]: filterAgentRange } : {})
        })
export const agentStatusURL = (telSysID: string, partitionID: string, deviceID: string): URLConstructor =>
    deviceBaseURL(telSysID, partitionID, deviceID).appendPathParams(APIPathParams.STATUS)

// Get current user info.
export const getCurrentUserURL = (): URLConstructor => getAPIBasePath().appendPathParams(APIPathParams.CURRENT_USER)
export const fallbackMenuURL = (servletPath: string, servletCmd: string): URLConstructor =>
    URLConstructor.newInstance({
        pathParams: [servletPath],
        queryParams: { ServletCmd: servletCmd }
    })

// Action URLS.
export const currentUserActionURL = (action: string): URLConstructor =>
    getAPIInternalBaseURL().appendPathParams(
        APIPathParams.CURRENT_USER,
        AkixiEntities.DEVICE,
        APIPathParams.ACTION,
        action
    )
export const actionAgentActionURL = (
    telSysID: string | number,
    partitionID: string | number,
    agentID: string,
    action: string
): URLConstructor => agentBaseURL(telSysID, partitionID, agentID).appendPathParams(APIPathParams.ACTION, action)
export const actionDeviceActionURL = (
    telSysID: string,
    partitionID: string,
    action: string,
    deviceID?: string
): URLConstructor => deviceBaseURL(telSysID, partitionID, deviceID).appendPathParams(APIPathParams.ACTION, action)
export const statusCallActionURL = (action: string): URLConstructor =>
    getAPIInternalBaseURL().appendPathParams(APIPathParams.ACTION, action, APIPathParams.RESULT)

// Get contact.
export const contactStatusURL: URLConstructor = getAPIInternalBaseURL().appendPathParams(
    AkixiEntities.CONTACT,
    APIPathParams.RESULT
)
export const contactURL = (telSysID: string, partitionID: string, callDetails?: CallDetailsInfo): URLConstructor => {
    if (callDetails) {
        const { fallbackID, itemID, itemIDNum, start: startTime, end: endTime }: CallDetailsInfo = callDetails
        const contactURLObject: URLConstructor = partitionBaseURL(telSysID, partitionID).appendPathParams(
            AkixiEntities.CONTACT
        )

        if (IsStringEmpty(itemID)) {
            return contactURLObject.appendQueryParams({
                fallbackID,
                itemIDNum,
                startTime,
                endTime
            })
        }

        return contactURLObject.appendPathParams(itemID).appendQueryParams({
            startTime,
            endTime
        })
    }

    return URLConstructor.newInstance()
}

export const callDetailsStatusURL: URLConstructor = getAPIInternalBaseURL().appendPathParams(
    AkixiEntities.CALL_DETAILS,
    APIPathParams.RESULT
)

export const callDetailsURL = (
    telSysID: string,
    partitionID: string,
    callDetails?: CallDetailsInfo
): URLConstructor => {
    if (callDetails) {
        const { fallbackID, start: startTime, end: endTime }: CallDetailsInfo = callDetails
        const contactURLObject: URLConstructor = partitionBaseURL(telSysID, partitionID).appendPathParams(
            AkixiEntities.CALL_DETAILS
        )

        return contactURLObject.appendPathParams(fallbackID as string).appendQueryParams({
            startTime,
            endTime
        })
    }

    return URLConstructor.newInstance()
}

// Get ACD & DND activity.
export const ACDDNDActivityStatusURL: URLConstructor = getAPIInternalBaseURL().appendPathParams(
    AkixiEntities.ACDDND,
    APIPathParams.DETAIL,
    APIPathParams.RESULT
)
export const ACDDNDActivityURL = (
    telSysID: string,
    partitionID: string,
    ACDDNDActivityItemID: string,
    entryTime: string
): string => {
    if (telSysID && partitionID && ACDDNDActivityItemID) {
        return partitionBaseURL(telSysID, partitionID)
            .appendPathParams(AkixiEntities.ACDDND, ACDDNDActivityItemID)
            .appendQueryParams({ entryTime })
            .constructURL()
    }

    return ''
}

// Get and Modify Directory Details
export const getDirectoryURL = (telSysId: string, partitionInTelSys: string): URLConstructor =>
    getAPIAdminBasePath().appendPathParams(
        AkixiEntities.TEL_SYS,
        telSysId,
        AkixiEntities.PARTITION,
        partitionInTelSys,
        AkixiEntities.DIRECTORY
    )

export const addDirectoryURL = (telSysID: string, partitionInTelSys: string): URLConstructor =>
    getDirectoryURL(telSysID, partitionInTelSys)

export const getDirectoryByTelNumber = (
    telSysId: string,
    partitionInTelSys: string,
    telNumber: string
): URLConstructor => getDirectoryURL(telSysId, partitionInTelSys).appendPathParams(telNumber)

export const updateDirectoryURL = (telSysId: string, partitionInTelSys: string, telNumber: string): URLConstructor =>
    getDirectoryByTelNumber(telSysId, partitionInTelSys, telNumber)

// Get call recordings list.
export const recordingStatusURL: URLConstructor = getAPIInternalBaseURL().appendPathParams(
    AkixiEntities.CALL,
    AkixiEntities.RECORDING,
    APIPathParams.RESULT
)

export const recordingsURL = (
    telSysID: string,
    telSysCallID: string,
    recordingID?: string,
    // TODO: when we transition away from the old JS make this either number or string only, it doesn't matter which.
    recordingStart?: number | string,
    capabilities?: Array<string>,
    numberToAssign?: number
): string => {
    const queryParams: Record<string, Definable<string | number | string[]>> = {
        startTime: recordingStart,
        numberToAssign,
        URLCapabilities: capabilities
    }

    return recordingBaseURL(telSysID, telSysCallID, recordingID).appendQueryParams(queryParams).constructURL()
}
export const recordingDetailsStatusURL: string = getAPIInternalBaseURL()
    .appendPathParams(AkixiEntities.CALL, AkixiEntities.RECORDING, APIPathParams.DETAIL, APIPathParams.RESULT)
    .constructURL()

// Get application user details.
export const userURL = (): URLConstructor => getAPIAdminBasePath().appendPathParams(AkixiEntities.USER)
export const userURLInternal = (): URLConstructor => getAPIInternalBaseURL().appendPathParams(AkixiEntities.USER)

/**
 * Builds the paginated admin application user list URL for a TelSys / partition scope with optional CME query scope.
 *
 * @param telSysID - Telephone system ID path segment (`-1` for all servers).
 * @param partitionIDInTelSys - Partition ID within the telephone system (`-1` for all partitions).
 * @param cmeID - CM Environment ID included as {@code CMEnvironmentID} when non-empty.
 * @param recursive - Whether owned child CMEs are included for non-root CME scopes.
 * @param offset - Zero-based pagination offset (paired with {@code limit}).
 * @param limit - Maximum number of rows to return.
 * @param sort - Optional server-side sort expression (for example {@code FullName:ASC}).
 * @param search - Optional case-insensitive search across username and full name.
 * @param userTypeFilter - Optional comma separated user-type filter ids; omit for no restriction.
 * @param userFields - Optional list of user field names per row.
 * @returns URL builder for the admin user list endpoint.
 */
export const usersAdminListURL = (
    telSysID: string | number,
    partitionIDInTelSys: string | number,
    cmeID: string,
    recursive?: boolean,
    offset?: number,
    limit?: number,
    sort?: string,
    search?: string,
    userTypeFilter?: string,
    userFields?: readonly string[],
    licenceFilter?: string,
    activityFilter?: string,
    lockedOutFilter?: boolean,
    pendingDeletionFilter?: boolean
): URLConstructor => {
    // NOSONAR
    const fieldsForList: readonly string[] =
        IsVarEmpty(userFields) || userFields.length === 0 ? USER_ADMIN_LIST_FIELDS : userFields

    const params: Record<string, Definable<QueryParamPrimitive>> = {
        [QueryParams.USER_FIELDS]: fieldsForList.map((field: string): string => field.trim()).join(COMMA),
        [QueryParams.RECURSIVE]: recursive ?? true,
        [QueryParams.OFFSET]: offset,
        [QueryParams.LIMIT]: limit,
        [QueryParams.SORT]: sort ?? 'FullName:ASC',
        [QueryParams.SEARCH_QUERY]: search
    }

    if (isStringNotEmpty(cmeID)) {
        params[QueryParams.CME_ID] = cmeID
    }

    // Each filter takes a comma separated list so several values can be selected at once; the server matches any
    // value within one filter and narrows across the filters.
    if (isStringNotEmpty(userTypeFilter)) {
        params[QueryParams.USER_TYPE_FILTER] = userTypeFilter
    }

    if (isStringNotEmpty(licenceFilter)) {
        params[QueryParams.LICENCE_FILTER] = licenceFilter
    }

    if (isStringNotEmpty(activityFilter)) {
        params[QueryParams.ACTIVITY_FILTER] = activityFilter
    }

    // Both state filters are sent as strings, not booleans: URLConstructor drops a false value, so passing the
    // boolean straight through would silently lose "not locked out" and "exclude pending deletion" - the filter
    // would appear to be applied while the request asked for everything.
    if (isVarNotEmpty(lockedOutFilter)) {
        params[QueryParams.LOCKED_OUT_FILTER] = lockedOutFilter ? TRUE : FALSE
    }

    // Left off entirely when neither state is required, which is what keeps pending deletion users listed.
    if (isVarNotEmpty(pendingDeletionFilter)) {
        params[QueryParams.PENDING_DELETION_FILTER] = pendingDeletionFilter ? TRUE : FALSE
    }

    return userURL()
        .appendPathParams(AkixiEntities.TEL_SYS, telSysID, AkixiEntities.PARTITION, partitionIDInTelSys)
        .appendQueryParams(params)
}

export const usersURLByTelSysPartition = (
    telSysID: string | number,
    partitionID: string | number,
    userFields?: string[]
): URLConstructor =>
    userURLInternal()
        .appendPathParams(AkixiEntities.TEL_SYS, telSysID, AkixiEntities.PARTITION, partitionID)
        .appendQueryParams({
            ...(isVarNotEmpty(userFields) && userFields.length > 0
                ? { [QueryParams.USER_FIELDS]: userFields.join(COMMA) ?? EMPTY_STRING }
                : {})
        })
export const getByUsernameURL = (username: string): URLConstructor => userURL().appendPathParams(username)

/**
 * Internal API: activity totals for one application user, shown behind the users list information icon.
 *
 * @param username - Application user id.
 * @returns URL for the user's more-information resource.
 */
export const userMoreInformationURL = (username: string): URLConstructor =>
    getAPIInternalBaseURL().appendPathParams(AkixiEntities.USER, username, APIPathParams.MORE_INFORMATION)

/**
 * Admin API: list copyable reports for the given application user (source of copy).
 *
 * @param username - Application user id path segment.
 * @returns URL builder for {@code GET .../user/{username}/copy-reports/reports}.
 */
export const userCopyReportsReportsURL = (username: string): URLConstructor =>
    getByUsernameURL(username).appendPathParams(APIPathParams.COPY_REPORTS, APIPathParams.COPY_REPORTS_REPORTS)

/**
 * Admin API: list target application users in scope for copying reports from the source user.
 *
 * @param username - Source application user id path segment.
 * @returns URL builder for {@code GET .../user/{username}/copy-reports/targets}.
 */
export const userCopyReportsTargetsURL = (username: string): URLConstructor =>
    getByUsernameURL(username).appendPathParams(APIPathParams.COPY_REPORTS, APIPathParams.COPY_REPORTS_TARGETS)

/**
 * Admin API: execute copy-reports from the source user to selected targets.
 *
 * @param username - Source application user id path segment.
 * @returns URL builder for {@code POST .../user/{username}/copy-reports/execute}.
 */
export const userCopyReportsExecuteURL = (username: string): URLConstructor =>
    getByUsernameURL(username).appendPathParams(APIPathParams.COPY_REPORTS, APIPathParams.COPY_REPORTS_EXECUTE)

/**
 * Admin API: sign in as the specified application user (session switch).
 *
 * @param username - Target application user id path segment.
 * @returns URL builder for {@code POST .../user/{username}/signInAs}.
 */
export const userSignInAsURL = (username: string): URLConstructor =>
    getByUsernameURL(username).appendPathParams(APIPathParams.SIGN_IN_AS)

export const usernameExistsURL = (username: string): URLConstructor =>
    getAPIBasePath().appendPathParams(APIPathParams.USERNAME, username)
export const sendEmailURL = (username: string): URLConstructor =>
    getByUsernameURL(username).appendPathParams(APIPathParams.SEND_EMAIL)

// Call Recording Integration
export const callRecIntegrationURL = (
    telSysID: string,
    partitionID: string | number,
    basePath: URLConstructor = getAPIAdminBasePath()
): URLConstructor =>
    basePath.appendPathParams(
        AkixiEntities.TEL_SYS,
        telSysID,
        AkixiEntities.PARTITION,
        partitionID,
        APIPathParams.CALL_RECORDING_INTEGRATIONS
    )

export const callRecIntegrationByGUIDURL = (integrationGUID: string): URLConstructor =>
    getAPIAdminBasePath().appendPathParams(APIPathParams.CALL_RECORDING_INTEGRATIONS, integrationGUID)

export const testCallRecIntegrationURL = (telSysID: string, partitionID: string): URLConstructor =>
    callRecIntegrationURL(telSysID, partitionID, getAPIInternalBaseURL()).appendPathParams(
        APIPathParams.TEST_CONNECTION
    )

export const testCallRecIntegrationResultURL: URLConstructor = getAPIInternalBaseURL().appendPathParams(
    APIPathParams.CALL_RECORDING_INTEGRATIONS,
    APIPathParams.TEST_CONNECTION,
    APIPathParams.RESULT
)

export const adminControlURL = (telsysID: string | number, partitionID: string | number): URLConstructor =>
    partitionURL(Number(telsysID), Number(partitionID)).appendPathParams(APIPathParams.ADMIN_CONTROL)
export const adminControlResultURL = (telsysID: string | number, partitionID: string | number): URLConstructor =>
    adminControlURL(telsysID, partitionID).appendPathParams(APIPathParams.RESULT)

export const callRecIntegrationConfigUrl = (
    telSysID: string,
    partitionID: string | number,
    integrationType: string,
    hostname: string
): URLConstructor =>
    callRecIntegrationURL(telSysID, partitionID, getAPIInternalBaseURL())
        .appendPathParams(APIPathParams.CONFIG)
        .appendQueryParams({
            [QueryParams.TYPE]: integrationType,
            [QueryParams.IP_ADDRESS_OR_HOSTNAME]: hostname
        })

// Get application Code details
export const addCodeURL = (telSysId: string, partitionInTelSys: string): URLConstructor =>
    partitionCreatorURL(telSysId).appendPathParams(partitionInTelSys, AkixiEntities.CODES)

export const updateCodeURL = (
    telSysId: string,
    partitionInTelSys: string,
    codeID: string,
    codeType: string
): URLConstructor => addCodeURL(telSysId, partitionInTelSys).appendPathParams(codeID, codeType)

export const getCodeByCodeNameURL = (
    telSysID: string,
    partitionID: string,
    code: string,
    codeType: string | number
): URLConstructor => partitionCreatorURL(telSysID).appendPathParams(partitionID, AkixiEntities.CODES, code, codeType)

// CME Details.
export const cmeAdminBaseURL = (): URLConstructor => getAPIAdminBasePath().appendPathParams(AkixiEntities.CME)
export const cmeDetailsByIDURL = (cmeID: string): URLConstructor =>
    cmeAdminBaseURL()
        .appendPathParams(cmeID)
        .appendQueryParams({ CMEnvironmentFields: ['BrandingConfig'] })
export const cmeDetailsByIDInternalURL = (cmeID: string): URLConstructor =>
    cmeInternalBaseURL().appendPathParams(cmeID, APIPathParams.INFO)

// Directory Entry
/** Returns the REST URL base for directory entry operations scoped to a telephony server and partition. */
export const directoryEntryBaseURL = (telSys: string | number, partition: string | number): URLConstructor =>
    partitionBaseURL(telSys, partition).appendPathParams(AkixiEntities.DIRECTORY)
export const directoryEntryByIdURL = (telSysID: string, partitionID: string, telNo: string): URLConstructor =>
    directoryEntryBaseURL(telSysID, partitionID).appendPathParams(telNo)

/**
 * Paginated directory entry list for a TelSys partition (internal API).
 *
 * @param telSysID - Telephony server ID.
 * @param partitionID - Partition ID in TelSys (numeric path segment).
 * @param offset - Zero-based offset.
 * @param limit - Page size.
 * @param sort - Sort expression (e.g. {@code Description:ASC}, {@code TelephoneNumber:ASC}).
 * @param search - Optional search (telephone number and description; server-side).
 */
export const directoryEntryListURL = (
    telSysID: string | number,
    partitionID: string | number,
    offset?: number,
    limit?: number,
    sort?: string,
    search?: string
): URLConstructor =>
    directoryEntryBaseURL(telSysID, partitionID).appendQueryParams({
        [QueryParams.OFFSET]: offset,
        [QueryParams.LIMIT]: limit,
        [QueryParams.SORT]: sort ?? SortingParameters.DESCRIPTION_ASC,
        [QueryParams.SEARCH_QUERY]: search
    })

/**
 * Directory entry CSV bulk upload (start / validate) for a single TelSys partition.
 *
 * <p>Uses the <strong>admin</strong> REST base ({@link partitionAdminBaseURL}), not {@link directoryEntryBaseURL},
 * which targets the internal API and does not expose bulk upload (would yield HTTP 405).
 *
 * @param telSysID - Telephony server ID.
 * @param partitionID - Partition ID within the TelSys.
 * @returns URL ending with {@code .../admin/telsys/{id}/partition/{pid}/directoryEntry/bulkUpload}.
 */
export const directoryEntryBulkUploadURL = (telSysID: string | number, partitionID: string | number): URLConstructor =>
    partitionAdminBaseURL(telSysID).appendPathParams(partitionID, AkixiEntities.DIRECTORY, 'bulkUpload')

// RequestGetJSON.
export const requestGetJsonURL = (params: Record<string, string>): URLConstructor =>
    getBaseServletPath().appendQueryParams(params)

// User roles
export const userRoleURL = (roleID?: string): URLConstructor =>
    getAPIAdminBasePath().appendPathParams(AkixiEntities.USER_ROLE, ...(roleID ? [roleID] : []))

// CME Estate details.
export const cmeInternalBaseURL = (): URLConstructor => getAPIInternalBaseURL().appendPathParams(AkixiEntities.CME)
export const cmeInternalGet = (cmeID: string): URLConstructor => cmeInternalBaseURL().appendPathParams(cmeID)
export const cmeDetailsInfoURL = (cmeID: string): URLConstructor =>
    cmeInternalBaseURL().appendPathParams(cmeID, APIPathParams.INFO)

// Make a call.
export const makeCallStatusURL: URLConstructor = statusCallActionURL(APIPathParams.MAKE_CALL)
export const makeCallMainDeviceURL = (): string => currentUserActionURL(APIPathParams.MAKE_CALL).constructURL()
export const makeCallFromDeviceURL = (telSysID: string, partitionID: string, deviceID: string): string =>
    actionDeviceActionURL(telSysID, partitionID, APIPathParams.MAKE_CALL, deviceID).constructURL()

// Transfer One Step.
export const transferOneStepStatusURL: URLConstructor = statusCallActionURL(APIPathParams.TRANSFER_ONE_STEP)
export const transferOneStepFromDeviceURL = (telSysID: string, partitionID: string, deviceID: string): string =>
    actionDeviceActionURL(telSysID, partitionID, APIPathParams.TRANSFER_ONE_STEP, deviceID).constructURL()
export const transferOneStepCurrentUser: string = currentUserActionURL(APIPathParams.TRANSFER_ONE_STEP).constructURL()

// Transfer One Step.
export const divertDeflectStatusURL: URLConstructor = statusCallActionURL(APIPathParams.DIVERT_DEFLECT)
export const divertDeflectFromDeviceURL = (telSysID: string, partitionID: string, deviceID: string): string =>
    actionDeviceActionURL(telSysID, partitionID, APIPathParams.DIVERT_DEFLECT, deviceID).constructURL()
export const divertDeflectCurrentUser: string = currentUserActionURL(APIPathParams.DIVERT_DEFLECT).constructURL()

// Inquiry transfer to.
export const inquiryTransferToStatusURL: URLConstructor = statusCallActionURL(APIPathParams.INQUIRY_TRANSFER_TO)
export const inquiryTransferToMainDeviceURL = (): string =>
    currentUserActionURL(APIPathParams.INQUIRY_TRANSFER_TO).constructURL()

// Pickup connection.=
export const pickupConnectionStatusURL: URLConstructor = statusCallActionURL(APIPathParams.PICKUP_CONNECTION)
export const pickupConnectionOnMainDeviceURL = (): string =>
    currentUserActionURL(APIPathParams.PICKUP_CONNECTION).constructURL()

// Set DND.
export const setDNDStatusURL = (dnd: boolean): URLConstructor =>
    statusCallActionURL(`${APIPathParams.SET_DND}${dnd ? DNDActions.DND_ON : DNDActions.DND_OFF}`)
export const setDNDMainDeviceURL = (telSysID: string, partitionID: string, deviceID: string, dnd: boolean): string =>
    actionDeviceActionURL(
        telSysID,
        partitionID,
        `${APIPathParams.SET_DND}${dnd ? DNDActions.DND_ON : DNDActions.DND_OFF}`,
        deviceID
    ).constructURL()

// Set ACD status.
export const setACDStatusStatusURL = (statusUpdateType: ACDStatusUpdates): URLConstructor =>
    statusCallActionURL(statusUpdateType)
export const setACDStatusAgentURLConstructor = (
    telSysID: string | number,
    partitionID: string | number,
    agentID: Nullable<string>,
    statusUpdateType: ACDStatusUpdates
): URLConstructor => actionAgentActionURL(telSysID, partitionID, agentID ?? '', statusUpdateType)
export const setACDStatusAgentURL = (
    telSysID: string,
    partitionID: string,
    agentID: Nullable<string>,
    statusUpdateType: ACDStatusUpdates
): string => setACDStatusAgentURLConstructor(telSysID, partitionID, agentID ?? '', statusUpdateType).constructURL()

// Call ctrl props.
export const callCtrlPropsURL: URLConstructor = getAPIInternalBaseURL().appendPathParams(
    APIPathParams.CURRENT_USER,
    AkixiEntities.CALL_CTRL_PROPS
)

// Get CMEs.
export const cmeURL: URLConstructor = cmeInternalBaseURL().appendQueryParams({
    [QueryParams.SORT]: SortingParameters.DESCRIPTION_ASC
})

export const getCMEURL = (fields?: string[]): URLConstructor =>
    cmeInternalBaseURL().appendQueryParams({
        [QueryParams.SORT]: SortingParameters.DESCRIPTION_ASC,
        [QueryParams.CME_FIELDS]: fields
    })

/**
 * Field names for paginated internal CME list ({@code CMEnvironmentFields}) for admin list parity.
 */
export const CME_ADMIN_LIST_FIELDS: readonly string[] = [
    'ListChildCMEnvironmentsCount',
    'ListTelSyssCount',
    'ListPartitionsCount',
    'ListUsersCount'
]

/**
 * Builds the paginated CM Environment list URL (internal API).
 *
 * @param offset - Zero-based offset.
 * @param limit - Page size.
 * @param sort - Sort expression (e.g. {@code Description:ASC}, {@code ID:ASC}).
 * @param search - Optional search query.
 * @param cmeFields - Optional extra CME fields per row.
 * @param parentCmeId - Optional parent CME ID to restrict results to children of that CME.
 * @param recursive - When {@code true} with {@code parentCmeId}, include all descendant CMEs; when {@code false}, direct children only.
 *     When {@code parentCmeId} is set and this argument is omitted, defaults to {@code true}.
 * @returns URL builder for the CME list endpoint.
 */
export const cmeListURL = (
    offset?: number,
    limit?: number,
    sort?: string,
    search?: string,
    cmeFields?: readonly string[],
    parentCmeId?: string,
    recursive?: boolean
): URLConstructor => {
    const requestedFields: Definable<string[]> = cmeFields ? [...cmeFields] : undefined
    const fields: readonly string[] = ArrayUtils.isEmpty(requestedFields) ? CME_ADMIN_LIST_FIELDS : requestedFields

    const params: Record<string, string | number | boolean | undefined> = {
        // NOSONAR
        [QueryParams.OFFSET]: offset,
        [QueryParams.LIMIT]: limit,
        [QueryParams.SORT]: sort ?? SortingParameters.DESCRIPTION_ASC,
        [QueryParams.SEARCH_QUERY]: search === EMPTY_STRING ? undefined : search,
        [QueryParams.CME_FIELDS]: fields.map((f: string): string => f.trim()).join(COMMA)
    }

    if (isStringNotEmpty(parentCmeId)) {
        params[QueryParams.PARENT_CME_ID] = parentCmeId
        params[QueryParams.RECURSIVE] = recursive ?? true
    }

    return cmeInternalBaseURL().appendQueryParams(params)
}

/** Admin API URL for a single CME by ID (e.g. DELETE). */
export const cmeAdminByIdURL = (cmeID: string): URLConstructor => cmeAdminBaseURL().appendPathParams(cmeID)

// Get All TelSys to user.
export const getAllTelSysAccessibleByUser = (): URLConstructor =>
    getAPIInternalBaseURL()
        .appendPathParams(AkixiEntities.TEL_SYS)
        .appendQueryParams({
            [QueryParams.RECURSIVE]: false,
            [QueryParams.SORT]: SortingParameters.DESCRIPTION_ASC
        })

// Get tel servers.
export const telServerURL = (
    cmeID: string,
    recursive?: boolean,
    cxiTelSysOnly?: boolean,
    telSysFields?: string[],
    ...licenceLevel: string[]
): URLConstructor =>
    getAPIInternalBaseURL()
        .appendPathParams(AkixiEntities.CME, cmeID, AkixiEntities.TEL_SYS)
        .appendQueryParams({
            [QueryParams.RECURSIVE]: recursive,
            [QueryParams.CXI_TELSYS_ONLY]: cxiTelSysOnly,
            [QueryParams.SORT]: SortingParameters.DESCRIPTION_ASC,
            [QueryParams.TEL_SYS_FIELDS]: [TelSysFields.DESCRIPTION, ...(telSysFields ?? [])]
                .map((telSysField: string): string => telSysField.trim())
                .join(COMMA),
            ...(isVarNotEmpty(licenceLevel) && licenceLevel.length > 0
                ? { [QueryParams.FILTER_BY_PARTITION_LICENCE]: licenceLevel.join(COMMA) }
                : {})
        })

/**
 * Optional list filters for {@link telSysListURL} / {@link getTelsysList}.
 */
export type TelsysListAPIFilters = {
    readonly platformType?: readonly number[]
    readonly reportLicence?: readonly string[]
    readonly moduleStatus?: readonly number[]
}

/**
 * Builds the paginated telephony server list URL for a CME scope.
 *
 * @param cmeID - CME identifier used as the listing scope.
 * @param recursive - Whether child CMEs should be included.
 * @param offset - Zero-based pagination offset.
 * @param limit - Maximum number of items to return.
 * @param sort - Optional server-side sort string.
 * @param search - Optional search query.
 * @param telSysFields - Optional list of extra Telephony Server fields to request.
 * @param listFilters - Optional column-aligned filters supported by the CME TelSys list API.
 * @returns URL builder configured for the telephony server list endpoint.
 */
export const telSysListURL = (
    cmeID: string,
    recursive?: boolean,
    offset?: number,
    limit?: number,
    sort?: string,
    search?: string,
    telSysFields?: string[],
    listFilters?: TelsysListAPIFilters
): URLConstructor => {
    // NOSONAR
    const fieldsForList: readonly string[] = ArrayUtils.isEmpty(telSysFields) ? TELSYS_ADMIN_LIST_FIELDS : telSysFields
    const filterClauses: string[] = []

    listFilters?.platformType?.forEach((id: number): void => {
        filterClauses.push(`PlatformType|${String(id)}`)
    })
    listFilters?.reportLicence?.forEach((token: string): void => {
        filterClauses.push(`ReportLicence|${token}`)
    })
    listFilters?.moduleStatus?.forEach((code: number): void => {
        filterClauses.push(`ModuleStatus|${String(code)}`)
    })

    const filterExpression: Definable<string> = filterClauses.length > 0 ? filterClauses.join(COMMA) : undefined

    return getAPIInternalBaseURL()
        .appendPathParams(AkixiEntities.CME, cmeID, AkixiEntities.TEL_SYS)
        .appendQueryParams({
            [QueryParams.RECURSIVE]: `${recursive || false}`,
            [QueryParams.OFFSET]: offset,
            [QueryParams.LIMIT]: limit,
            [QueryParams.SORT]: sort ?? SortingParameters.DESCRIPTION_ASC,
            [QueryParams.SEARCH_QUERY]: search,
            [QueryParams.TEL_SYS_FIELDS]: fieldsForList
                .map((telSysField: string): string => telSysField.trim())
                .join(COMMA),
            ...(filterExpression ? { [QueryParams.FILTER]: filterExpression } : {})
        })
}

/**
 * Builds the paginated partition list URL for a TelSys scope (admin API).
 *
 * @param telSysID - TelSys identifier.
 * @param offset - Zero-based pagination offset.
 * @param limit - Maximum number of items to return.
 * @param sort - Optional server-side sort string.
 * @param search - Optional search query.
 * @param partitionIDInTelSys - Optional partition self-filter for servlet parity.
 * @param partitionFields - Optional list of partition field names per row.
 * @returns URL builder configured for the partition list endpoint.
 */
export const partitionAdminListURL = (
    telSysID: string | number,
    offset?: number,
    limit?: number,
    sort?: string,
    search?: string,
    partitionIDInTelSys?: string | number,
    licenceFilter?: string,
    partitionFields?: string[],
    syncTypeFilter?: string,
    statusFilter?: string
): URLConstructor => {
    // NOSONAR
    const fieldsForList: readonly string[] = ArrayUtils.isEmpty(partitionFields)
        ? PARTITION_ADMIN_LIST_FIELDS
        : partitionFields

    const params: Record<string, string | number | boolean | undefined> = {
        [QueryParams.OFFSET]: offset,
        [QueryParams.LIMIT]: limit,
        [QueryParams.SORT]: sort ?? SortingParameters.DESCRIPTION_ASC,
        [QueryParams.PARTITION_FIELDS]: fieldsForList.map((field: string): string => field.trim()).join(COMMA),
        [QueryParams.SEARCH_QUERY]: search,
        search
    }

    if (isVarNotEmpty(partitionIDInTelSys) && partitionIDInTelSys !== EMPTY_STRING) {
        params[QueryParams.PARTITION_ID_IN_TELSYS] = partitionIDInTelSys
    }

    if (isStringNotEmpty(licenceFilter)) {
        params[QueryParams.FILTER_BY_PARTITION_LICENCE] = licenceFilter
    }

    if (isStringNotEmpty(syncTypeFilter)) {
        params[QueryParams.FILTER_BY_PARTITION_SYNC_TYPE] = syncTypeFilter
    }

    if (isStringNotEmpty(statusFilter)) {
        params[QueryParams.FILTER_BY_PARTITION_STATUS] = statusFilter
    }

    return partitionAdminBaseURL(telSysID).appendQueryParams(params)
}

/**
 * Builds the paginated code list URL for a CME scope (internal API).
 *
 * @param cmeID - CME identifier used as the listing scope.
 * @param recursive - Whether child CMEs should be included.
 * @param telSysID - Optional TelSys ID to restrict scope.
 * @param partIDInTelSys - Optional partition ID in TelSys to restrict scope.
 * @param codeTypeFilter - Optional code type filter (e.g. comma-separated type IDs).
 * @param codeScopeFilter - Optional scope filter (1=service-wide, 2=system-wide, 10=detached).
 * @param offset - Zero-based pagination offset.
 * @param limit - Maximum number of items to return.
 * @param sort - Optional server-side sort string.
 * @param search - Optional search query.
 * @param codeFields - Optional list of code field names per row.
 * @returns URL builder for the code list endpoint.
 */
export const codeListURL = (
    cmeID: string,
    recursive?: boolean,
    telSysID?: string | number,
    partIDInTelSys?: string | number,
    codeTypeFilter?: string,
    offset?: number,
    limit?: number,
    sort?: string,
    search?: string,
    codeFields?: string[],
    codeScopeFilter?: string
): URLConstructor => {
    // NOSONAR
    const fieldsForList: readonly string[] = ArrayUtils.isEmpty(codeFields) ? CODE_ADMIN_LIST_FIELDS : codeFields

    const params: Record<string, string | number | boolean | undefined> = {
        [QueryParams.RECURSIVE]: recursive ?? false,
        [QueryParams.OFFSET]: offset,
        [QueryParams.LIMIT]: limit,
        [QueryParams.SORT]: sort ?? SortingParameters.DESCRIPTION_ASC,
        [QueryParams.SEARCH_QUERY]: search,
        [QueryParams.CODE_FIELDS]: fieldsForList.map((f: string): string => f.trim()).join(COMMA)
    }
    if (isVarNotEmpty(telSysID) && telSysID !== EMPTY_STRING) {
        params[QueryParams.TEL_SYS_ID] = telSysID
    }
    if (isVarNotEmpty(partIDInTelSys) && partIDInTelSys !== EMPTY_STRING) {
        params[QueryParams.PARTITION_ID_IN_TELSYS] = partIDInTelSys
    }
    if (isStringNotEmpty(codeTypeFilter)) {
        params[QueryParams.CODE_TYPE_FILTER] = codeTypeFilter
    }
    if (isStringNotEmpty(codeScopeFilter)) {
        params[QueryParams.CODE_SCOPE_FILTER] = codeScopeFilter
    }

    return getAPIInternalBaseURL().appendPathParams(AkixiEntities.CME, cmeID, 'codes').appendQueryParams(params)
}

/**
 * Builds the paginated recording integration list URL for a CME scope (internal API).
 */
export const recordingListURL = (
    cmeID: string,
    recursive?: boolean,
    telSysID?: string | number,
    partIDInTelSys?: string | number,
    callRecIntTypeFilter?: string,
    offset?: number,
    limit?: number,
    sort?: string,
    search?: string,
    callRecIntFields?: string[],
    callRecIntScopeFilter?: string
): URLConstructor => {
    // NOSONAR
    const fieldsForList: readonly string[] = ArrayUtils.isEmpty(callRecIntFields)
        ? RECORDING_ADMIN_LIST_FIELDS
        : callRecIntFields

    const params: Record<string, string | number | boolean | undefined> = {
        [QueryParams.RECURSIVE]: recursive ?? false,
        [QueryParams.OFFSET]: offset,
        [QueryParams.LIMIT]: limit,
        [QueryParams.SORT]: sort ?? SortingParameters.DESCRIPTION_ASC,
        [QueryParams.SEARCH_QUERY]: search,
        [QueryParams.CALL_REC_INT_FIELDS]: fieldsForList.map((f: string): string => f.trim()).join(COMMA)
    }
    if (isVarNotEmpty(telSysID) && telSysID !== EMPTY_STRING) {
        params[QueryParams.TEL_SYS_ID] = telSysID
    }
    if (isVarNotEmpty(partIDInTelSys) && partIDInTelSys !== EMPTY_STRING) {
        params[QueryParams.PARTITION_ID_IN_TELSYS] = partIDInTelSys
    }
    if (isStringNotEmpty(callRecIntTypeFilter)) {
        params[QueryParams.CALL_REC_INT_TYPE_FILTER] = callRecIntTypeFilter
    }
    if (isStringNotEmpty(callRecIntScopeFilter)) {
        params[QueryParams.CALL_REC_INT_SCOPE_FILTER] = callRecIntScopeFilter
    }

    return getAPIInternalBaseURL()
        .appendPathParams(AkixiEntities.CME, cmeID, APIPathParams.CALL_RECORDING_INTEGRATIONS)
        .appendQueryParams(params)
}

/**
 * Builds the paginated directory entry list URL for a CME scope (internal API).
 *
 * @param cmeID - CME identifier used as the listing scope.
 * @param recursive - Whether child CMEs should be included.
 * @param telSysID - Optional TelSys ID to restrict scope.
 * @param partIDInTelSys - Optional partition ID in TelSys to restrict scope.
 * @param offset - Zero-based pagination offset.
 * @param limit - Maximum number of items to return.
 * @param sort - Optional server-side sort string.
 * @param search - Optional search query.
 * @returns URL builder for the CME directory list endpoint.
 */
export const directoryListForCMEURL = (
    cmeID: string,
    recursive?: boolean,
    telSysID?: string | number,
    partIDInTelSys?: string | number,
    offset?: number,
    limit?: number,
    sort?: string,
    search?: string
): URLConstructor => {
    // NOSONAR
    const params: Record<string, string | number | boolean | undefined> = {
        [QueryParams.RECURSIVE]: recursive ?? false,
        [QueryParams.OFFSET]: offset,
        [QueryParams.LIMIT]: limit,
        [QueryParams.SORT]: sort ?? SortingParameters.DESCRIPTION_ASC,
        [QueryParams.SEARCH_QUERY]: search
    }
    if (typeof telSysID === 'number' ? isVarNotEmpty(telSysID) : isStringNotEmpty(telSysID)) {
        params[QueryParams.TEL_SYS_ID] = telSysID
    }
    if (typeof partIDInTelSys === 'number' ? isVarNotEmpty(partIDInTelSys) : isStringNotEmpty(partIDInTelSys)) {
        params[QueryParams.PARTITION_ID_IN_TELSYS] = partIDInTelSys
    }

    return getAPIInternalBaseURL()
        .appendPathParams(AkixiEntities.CME, cmeID, APIPathParams.CME_DIRECTORY)
        .appendQueryParams(params)
}

// Get tel server.
export const telSysBaseURL: URLConstructor = getAPIInternalBaseURL().appendPathParams(AkixiEntities.TEL_SYS)
export const telSysURL = (telServerID: string | number, ...telSysFields: string[]): URLConstructor =>
    telSysBaseURL.appendPathParams(telServerID).appendQueryParams({
        ...(telSysFields && telSysFields.length > 0
            ? { [QueryParams.TEL_SYS_FIELDS]: telSysFields.join(COMMA) }
            : undefined),
        // Check if telSysFields contains values from advanced fields and add advancedFields to the query params
        ...(Object.values(ADVANCED_FIELDS).some((field: string): boolean => telSysFields.includes(field))
            ? { [QueryParams.ADVANCED_FIELDS_PARAM]: true }
            : {})
    })
// Get partition.
export const partitionURL = (telServerID: number, partitionID: number, ...partitionFields: string[]): URLConstructor =>
    getAPIInternalBaseURL()
        .appendPathParams(AkixiEntities.TEL_SYS, telServerID, AkixiEntities.PARTITION, partitionID)
        .appendQueryParams({
            [QueryParams.PARTITION_FIELDS]: partitionFields?.join(COMMA)
        })

// Get partitions.
export const partitionsURL = (telServerID: number): URLConstructor =>
    getAPIInternalBaseURL()
        .appendPathParams(AkixiEntities.TEL_SYS, telServerID, AkixiEntities.PARTITION)
        .appendQueryParams({ [QueryParams.SORT]: SortingParameters.DESCRIPTION_ASC })

// Get Group membership.
export const groupMembershipURL = (
    telServerID: string | number,
    partitionID: string | number,
    device: string,
    deviceFilter?: string[],
    searchQuery?: string,
    deviceFields?: string,
    limit?: number,
    offset?: number,
    scopeGroupHierarchy?: boolean
): URLConstructor =>
    getAPIInternalBaseURL()
        .appendPathParams(
            AkixiEntities.TEL_SYS,
            telServerID,
            AkixiEntities.PARTITION,
            partitionID,
            AkixiEntities.DEVICE,
            device,
            AkixiEntities.GROUP_MEMBERS
        )
        .appendQueryParams({
            [QueryParams.DEVICE_TYPE_FILTER]: deviceFilter?.join('-'),
            [QueryParams.SEARCH_QUERY]: searchQuery,
            [QueryParams.DEVICE_FIELDS]: deviceFields,
            ...(isVarNotEmpty(limit) ? { [QueryParams.LIMIT]: limit } : {}),
            ...(isVarNotEmpty(offset) ? { [QueryParams.OFFSET]: offset } : {}),
            ...(isVarNotEmpty(scopeGroupHierarchy)
                ? { [QueryParams.SCOPE_GROUP_HIERARCHY]: `${scopeGroupHierarchy}` }
                : {})
        })

// Get agents.
export const agentAdminURL = (telSysID: number, partitionID: number, agentNumber: string): URLConstructor =>
    partitionUpdateURL(telSysID, partitionID).addPathParams(AkixiEntities.ACD_AGENTS, agentNumber)

export const agentsAdminURL = (telServerID: number, partitionID: number): URLConstructor =>
    getAPIAdminBasePath().appendPathParams(
        AkixiEntities.TEL_SYS,
        telServerID,
        AkixiEntities.PARTITION,
        partitionID,
        AkixiEntities.ACD_AGENTS
    )

// Get partition.
export const callRecordingIntegrationsURL = (cmeID: string, telServerID: number, partitionID: number): URLConstructor =>
    getAPIInternalBaseURL()
        .appendPathParams(
            AkixiEntities.CME,
            cmeID,
            AkixiEntities.TEL_SYS,
            telServerID,
            AkixiEntities.PARTITION,
            partitionID,
            APIPathParams.CALL_RECORDING_INTEGRATIONS
        )
        .appendQueryParams({
            [QueryParams.RECURSIVE]: 'true'
        })

// Get servlet config.
export const CONFIG_PASSWORD_URL: URLConstructor = getAPIInternalBaseURL().appendPathParams(
    APIPathParams.CONFIG,
    AkixiEntities.PASSWORD
)
export const CONFIG_LANGUAGE_URL: URLConstructor = getAPIInternalBaseURL().appendPathParams(
    APIPathParams.CONFIG,
    AkixiEntities.LANGUAGE
)

// Get partition by GUID.
export const partitionByGUIDURL = (partitionGUID: string): URLConstructor =>
    getAPIInternalBaseURL().appendPathParams(AkixiEntities.PARTITION, partitionGUID)

// Get default reports list.
export const defaultReportsListURL = (reportingLevel: string, telSysID?: number): URLConstructor =>
    getAPIInternalBaseURL()
        .appendPathParams(AkixiEntities.DEFAULT_REPORTS, reportingLevel)
        .appendQueryParams({
            [QueryParams.TEL_SYS_ID]: telSysID
        })

// Feature info.
export const FEATURE_INFO_URL: URLConstructor = getAPIInternalBaseURL().appendPathParams(APIPathParams.FEATURE)

// Insights.
export const insightsURL = (telSys: string | number, partition: string | number): URLConstructor =>
    partitionUpdateURL(telSys, partition).appendPathParams(APIPathParams.INSIGHTS, APIPathParams.REPORT)

// MFA.
export const mfaSecretCodeURL: URLConstructor = getCurrentUserURL().appendPathParams(
    MFAPathParams.MFA,
    MFAPathParams.SECRET_CODE
)

export const mfaSetupURL: URLConstructor = getCurrentUserURL().appendPathParams(MFAPathParams.MFA, MFAPathParams.SETUP)

export const mfaEnableURL: URLConstructor = getCurrentUserURL().appendPathParams(
    MFAPathParams.MFA,
    MFAPathParams.ENABLE
)

export const mfaOTPURL: URLConstructor = getCurrentUserURL().appendPathParams(MFAPathParams.MFA, MFAPathParams.OTP)

export const loginURL: URLConstructor = getAPIBasePath().appendPathParams(LogInOut.LOG_IN)

export const logoutURL: URLConstructor = getAPIBasePath().appendPathParams(LogInOut.LOG_OUT)

// Invite User
export const inviteUserURL: URLConstructor = getAPIIntegrationBaseURL().appendPathParams(
    APIPathParams.TRACKER,
    APIPathParams.INVITE,
    APIPathParams.FINISH
)

// Notifications settings
export const currentUserSettings: URLConstructor = getAPIInternalBaseURL().appendPathParams(
    APIPathParams.CURRENT_USER,
    APIPathParams.SETTINGS
)

export const notificationSettingsPushToken: URLConstructor = currentUserSettings.appendPathParams(
    APIPathParams.NOTIFICATION_PUSH_TOKEN
)

export const ssoLoginURL = (ssoCode: string): URLConstructor =>
    getAPIBasePath()
        .appendPathParams(SSOLogIn.SSO)
        .appendQueryParams({ [QueryParams.CODE]: ssoCode })

export const brandingURL = getAPIInternalBaseURL()
    .appendPathParams(APIPathParams.BRANDING)
    .appendQueryParams({ [QueryParams.DEFAULT_BRANDING_STYLE]: 'true' })

export const brandingsURL = getAPIInternalBaseURL()
    .appendPathParams(APIPathParams.BRANDING, APIPathParams.LIST)
    .appendQueryParams({ [QueryParams.DEFAULT_BRANDING_STYLE]: 'true' })

export const defaultReportCollectionNameURL = getAPIInternalBaseURL().appendPathParams(
    APIPathParams.REPORT,
    APIPathParams.DEFAULT_REPORT
)

export const defaultReportSetCreationURL = (defaultReportType: string): URLConstructor =>
    getAPIInternalBaseURL()
        .appendPathParams(APIPathParams.REPORT, APIPathParams.REPORT_SET)
        .appendQueryParams({ [QueryParams.DEFAULT_REPORT_TYPE]: defaultReportType })

export const defaultReportCreationURL = (defaultReportType: string, defaultReportName: string): URLConstructor =>
    getAPIInternalBaseURL()
        .appendPathParams(APIPathParams.REPORT, APIPathParams.DEFAULT_REPORT)
        .appendQueryParams({
            [QueryParams.DEFAULT_REPORT_TYPE]: defaultReportType,
            [QueryParams.DEFAULT_REPORT_NAME]: defaultReportName
        })

export const moveReportsToBinMURL = (moveToBin: boolean): URLConstructor =>
    getAPIInternalBaseURL()
        .appendPathParams(APIPathParams.REPORT, APIPathParams.MOVE_TO_BIN)
        .appendQueryParams({ [QueryParams.MOVE_TO_BIN]: `${moveToBin || false}` })

export const broadworksAuthenticateURL: URLConstructor = getAPIInternalBaseURL().appendPathParams(
    APIPathParams.BROADWORKS,
    APIPathParams.AUTHENTICATE
)

export const getDeviceByIDOnTelSysURL = (idOnTelSys: string): URLConstructor =>
    getAPIInternalBaseURL().appendPathParams(AkixiEntities.DEVICE, APIPathParams.ID_ON_TEL_SYS, idOnTelSys)

export const postBroadworksSignupURL: URLConstructor = getAPIInternalBaseURL().appendPathParams(
    APIPathParams.BROADWORKS,
    APIPathParams.SIGNUP
)

export const getTimezoneDataURL: URLConstructor = getAPIInternalBaseURL().appendPathParams(
    APIPathParams.UTILITIES,
    APIPathParams.TIMEZONE
)

export const TIMEZONE_DEFAULT_URL: URLConstructor = getTimezoneDataURL.appendPathParams(APIPathParams.DEFAULT)

export const LICENCE_URL: URLConstructor = getAPIInternalBaseURL().appendPathParams(APIPathParams.LICENCE)

export type LicenseManagementReportType = 'billingSummary' | 'billingCdr' | 'diagnostic'

export type LicenseManagementReportQueryParams = {
    readonly CMEnvironmentID: string
    readonly telSysID: string | number
    readonly partitionIDInTelSys: string
    readonly onlyBillableConfiguration: boolean
    readonly includeSystemWideUsers: boolean
    readonly includeBillingExcludedConfigurations: boolean
    readonly includeSimulationTelephoneSystems: boolean
    readonly includeNonBillingExcludedConfigurations: boolean
}

export const licenceStatusURL: URLConstructor = LICENCE_URL.appendPathParams(APIPathParams.STATUS)

export const licenceReloadURL: URLConstructor = LICENCE_URL.appendPathParams(APIPathParams.ACTION, APIPathParams.RELOAD)

export const MESSAGES_URL: URLConstructor = getAPIInternalBaseURL().appendPathParams(APIPathParams.MESSAGES)

export const DATABASE_URL: URLConstructor = getAPIInternalBaseURL().appendPathParams(APIPathParams.DATABASE)

/** Returns the REST URL for database settings. */
export const databaseSettingsURL = (): URLConstructor => DATABASE_URL.appendPathParams(APIPathParams.SETTINGS)

/** Returns the REST URL for listing message definition types. */
export const getMessagesTypesURL = (): URLConstructor => MESSAGES_URL.appendPathParams(APIPathParams.TYPES)

/** Returns the REST URL for listing message definition languages. */
export const getMessagesLanguagesURL = (): URLConstructor => MESSAGES_URL.appendPathParams(APIPathParams.LANGUAGES)

/** Returns the REST URL for a specific message definition by type ID and locale. */
export const getMessageDefinitionURL = (typeId: string, locale: string): URLConstructor =>
    MESSAGES_URL.appendPathParams(typeId, IsStringEmpty(locale) ? APIPathParams.DEFAULT : locale)

export const getLicenceSettingsURL = (licenceSettings?: string[]): URLConstructor =>
    LICENCE_URL.appendPathParams(APIPathParams.SETTINGS).appendQueryParams({
        [QueryParams.LICENCE_SETTING_FIELDS]: licenceSettings?.join(COMMA)
    })

/** Returns the REST URL for the licence management report. */
export const getLicenseManagementReportURL = (
    reportType: LicenseManagementReportType,
    query: LicenseManagementReportQueryParams
): URLConstructor => LICENCE_URL.appendPathParams(APIPathParams.REPORT, reportType).appendQueryParams(query)

export const getCallRecordingIntegrationURL = (telSysID: string | number): URLConstructor =>
    telSysURL(telSysID).appendPathParams(APIPathParams.CALL_RECORDING_INTEGRATIONS)

const INTEGRATION_TYPES: Record<string, string> = {
    [TelSysTypes.WEBEX_CALLING]: 'webexCalling',
    [TelSysTypes.MS_TEAMS]: 'MSTeams'
}

export const getOAuthConfigURL = (telSysType: TelSysTypes): URLConstructor =>
    getAPIBasePath().appendPathParams(
        APIPathParams.INTEGRATION,
        APIPathParams.OAUTH,
        INTEGRATION_TYPES[telSysType],
        APIPathParams.CONFIG
    )

export const getOAuthIntegrationStatus = (telSysType: string, sessionID: string | number): URLConstructor =>
    getAPIBasePath().appendPathParams(
        APIPathParams.INTEGRATION,
        APIPathParams.OAUTH,
        INTEGRATION_TYPES[telSysType],
        sessionID,
        APIPathParams.STATUS
    )

export const getSimulationModesURL = (): URLConstructor =>
    telSysBaseURL.appendPathParams(APIPathParams.CALL_CONTROL_OPTIONS)

export const cmeLicencesURL = (cmeID: string): URLConstructor =>
    cmeInternalGet(cmeID).appendPathParams(APIPathParams.LICENCES)

export const telSysLicencesURL = (telSysID: string | number): URLConstructor =>
    telSysByIDInteralURL(telSysID).appendPathParams(APIPathParams.LICENCES)

export const changeCurrentUserPasswordURL = (): URLConstructor =>
    getCurrentUserURL().appendPathParams(APIPathParams.CHANGE_PASSWORD)

export const passwordURL = (): URLConstructor => getAPIBasePath().appendPathParams(APIPathParams.PASSWORD)

export const generatePasswordURL = (): URLConstructor => passwordURL().appendPathParams(APIPathParams.GENERATE)

export const passwordStrengthURL = (): URLConstructor => passwordURL().appendPathParams(APIPathParams.STRENGTH)

export const telSysCallCaptureControlURL = (telSysID: string | number): URLConstructor =>
    telSysUpdateURL(telSysID).appendPathParams(APIPathParams.CALL_CAPTURE)

export const cmeCallCaptureControlURL = (cmeID: string): URLConstructor =>
    cmeDetailsByIDURL(cmeID).appendPathParams(APIPathParams.CALL_CAPTURE)

export const hideInsightsHub: URLConstructor = getCurrentUserURL().appendPathParams(APIPathParams.HIDE_INSIGHTS_HUB)

export const showInsightsHub: URLConstructor = getCurrentUserURL().appendPathParams(APIPathParams.SHOW_INSIGHTS_HUB)

export const PRESENCE_WEBSOCKET_URL: URLConstructor = URLConstructor.newWebsocketURL({
    pathParams: [APIElements.CCS, APIPathParams.WEBSOCKET, APIPathParams.PRESENCE]
})

export const deviceTypesURL: URLConstructor = getAPIInternalBaseURL().appendPathParams(
    APIPathParams.REPORT,
    APIPathParams.FILTER_OPTION,
    APIPathParams.DEVICE_TYPES
)

export const LOCALES_URL: URLConstructor = getAPIInternalBaseURL().appendPathParams(APIPathParams.LOCALES)

// API Key URL under user URL.
export const getAPIKeyURL = (username: string, id?: string): URLConstructor =>
    getAPIInternalBaseURL().appendPathParams(
        APIPathParams.USER,
        username,
        APIPathParams.API_KEYS,
        ...(isStringNotEmpty(id) ? [id] : [])
    )

export const ROUTE_URL: URLConstructor = getAppV2APIBasePath().appendPathParams(APIPathParams.ROUTE)

export const AUTH_OUTFLOW_URL: URLConstructor = ROUTE_URL.appendPathParams(APIPathParams.AUTH_OUTFLOW)

export const acceptTermsOfServiceURL: URLConstructor = getCurrentUserURL().appendPathParams(
    APIPathParams.ACCEPT_TERMS_OF_SERVICE
)

export const rejectTermsOfServiceURL: URLConstructor = getCurrentUserURL().appendPathParams(
    APIPathParams.REJECT_TERMS_OF_SERVICE
)

export const LOGIN_LANDING_PAGE_FLAGS_URL: URLConstructor = getAPIInternalBaseURL().appendPathParams(
    APIPathParams.LICENCE,
    APIPathParams.LOGIN_LANDING
)

export const userLoginLandingUserCheckURL = (username: string, cme?: string): URLConstructor =>
    getAPIBasePath().appendPathParams(APIPathParams.USER, username).appendQueryParams({ cme })

export const availableServicesForUser = (username: string, cme?: string): URLConstructor =>
    getAPIBasePath().appendPathParams(APIPathParams.USER, username, APIPathParams.SERVICES).appendQueryParams({ cme })

export const RESET_PASSWORD_URL: URLConstructor = getCurrentUserURL().appendPathParams(APIPathParams.RESET_PASSWORD)

export const SESSION_LANGUAGE_URL: URLConstructor = getAPIInternalBaseURL().appendPathParams(
    APIPathParams.SESSION,
    APIPathParams.LANGUAGE
)

export const SSO_LOGIN_URL: URLConstructor = getAPIBasePath().appendPathParams(APIPathParams.LOGIN)

export const INTERNAL_LOGIN_URL: URLConstructor = getAPIBasePath().appendPathParams(
    APIPathParams.CURRENT_USER,
    APIPathParams.LOGIN
)

export const RECAPTCHA_CONFIG_URL: URLConstructor = getAPIInternalBaseURL().appendPathParams(
    APIPathParams.CONFIG,
    APIPathParams.RECAPTCHA
)

export const FORGOT_CREDENTIALS_URL: URLConstructor = getCurrentUserURL().appendPathParams(
    APIPathParams.FORGOT_CREDENTIALS
)

export const USER_FAVOURITES_URL: URLConstructor = currentUserSettings.appendPathParams(APIPathParams.FAVOURITES)

export const USER_DARKMODE_URL: URLConstructor = currentUserSettings.appendPathParams(APIPathParams.DARK_MODE)

export const mappedTimezoneURL = (timezone: string): URLConstructor =>
    getAPIInternalBaseURL()
        .appendPathParams(APIPathParams.TIMEZONE)
        .appendQueryParams({
            [QueryParams.IANA]: timezone
        })

export const SESSION_TOKEN_URL: URLConstructor = getAPIInternalBaseURL().appendPathParams(
    APIPathParams.SESSION,
    APIPathParams.TOKEN
)

export const AUTH_TOKEN_URL: URLConstructor = getAPIBasePath().appendPathParams(APIPathParams.AUTH, APIPathParams.TOKEN)

export const REFRESH_TOKEN_URL: URLConstructor = getAPIBasePath().appendPathParams(
    APIPathParams.AUTH,
    APIPathParams.REFRESH
)

export const getServletCommandURL = (branding: string, servletCmd: string): URLConstructor =>
    getBaseServletPath(branding?.replace(SLASH, EMPTY_STRING)).appendQueryParams({ ServletCmd: servletCmd })

export const USER_AGENTS_URL: URLConstructor = getAPIInternalBaseURL().appendPathParams(
    APIPathParams.SESSION,
    APIPathParams.USER_AGENTS
)

export const USER_AGENT_URL: URLConstructor = getAPIInternalBaseURL().appendPathParams(
    APIPathParams.SESSION,
    APIPathParams.USER_AGENT
)

export const getBrandingConfigByCMEAndTypeURL = (cmeID: string, brandingType: string): URLConstructor =>
    getAPIInternalBaseURL().appendPathParams(AkixiEntities.CME, cmeID, APIPathParams.BRANDING_CONFIG, brandingType)

export const getBrandingIDSessionURL = (brandingID?: string): URLConstructor =>
    getAPIInternalBaseURL()
        .appendPathParams(APIPathParams.SESSION, APIPathParams.BRANDING)
        .appendQueryParams(
            isStringNotEmpty(brandingID)
                ? {
                      previewBrandingID: brandingID
                  }
                : {}
        )

export const getBrandingVideoUploadURL = (brandingID: string): URLConstructor =>
    getAPIInternalBaseURL()
        .appendPathParams(APIPathParams.BRANDING, APIPathParams.VIDEO)
        .appendQueryParams({ [QueryParams.BRANDING_ID]: brandingID })

export const getQueueReferences = (
    telsys: string | number,
    partition: string | number,
    idOnTelSysOwner: string
): URLConstructor =>
    partitionBaseURL(telsys, partition).appendPathParams(
        AkixiEntities.DEVICE,
        APIPathParams.ID_ON_TEL_SYS_OWNER,
        idOnTelSysOwner,
        APIPathParams.REFERENCES
    )

export const getClientConfigurationURL = (
    telsysID: string | number,
    partition: string | number,
    deviceID: string,
    id?: string
): URLConstructor =>
    deviceURL(telsysID.toString(), partition.toString(), deviceID)
        .appendPathParams(APIPathParams.INTEGRATOR_CONFIG, id ?? EMPTY_STRING)
        .appendQueryParams({
            [QueryParams.INTEGRATOR_CLIENT_FIELDS]: 'Policies,Settings'
        })

export const getClientConfigurationListURL = (
    telsysID: string | number,
    partition: string | number,
    deviceID: string,
    offset?: number,
    limit?: number,
    sort?: string,
    search?: string
): URLConstructor =>
    deviceURL(telsysID.toString(), partition.toString(), deviceID)
        .appendPathParams(APIPathParams.INTEGRATOR_CONFIG)
        .appendQueryParams({
            [QueryParams.OFFSET]: offset,
            [QueryParams.LIMIT]: limit,
            [QueryParams.SORT]: sort,
            [QueryParams.SEARCH_QUERY]: search,
            [QueryParams.INTEGRATOR_CLIENT_FIELDS]: 'TelSysDescription,PartitionDescription,DeviceDescription'
        })

const resolveCRMNumericPathParam = (value?: number): string =>
    isVarNotEmpty(value) && !Number.isNaN(Number(value)) ? value.toString() : ANY_VALUE_STRING_NUMERIC

const resolveCRMDevicePathParam = (deviceID?: string): string =>
    isStringNotEmpty(deviceID) ? deviceID : ANY_DEVICE_VALUE

export const getCRMConfigurationURL = (
    telsysID: number,
    partition: number,
    deviceID: string,
    id?: string
): URLConstructor =>
    deviceURL(
        resolveCRMNumericPathParam(telsysID),
        resolveCRMNumericPathParam(partition),
        resolveCRMDevicePathParam(deviceID)
    ).appendPathParams(APIPathParams.CRM_CONFIGURATION, APIPathParams.CONFIGS, id ?? EMPTY_STRING)

export const getOAuthCrmConfigurationURL = (crmConfigId: string): URLConstructor =>
    getAPIBasePath().appendPathParams(
        APIPathParams.INTEGRATION,
        APIPathParams.OAUTH,
        APIPathParams.CRM_CONFIGURATION,
        crmConfigId,
        APIPathParams.CONFIG
    )

export const getCRMOAuthStatus = (sessionId: number): URLConstructor =>
    getAPIBasePath().appendPathParams(
        APIPathParams.INTEGRATION,
        APIPathParams.OAUTH,
        APIPathParams.CRM_CONFIGURATION,
        sessionId,
        APIPathParams.STATUS
    )

export const getCRMConfigurationListURL = (
    telsysID: string | number,
    partition: string | number,
    deviceID: string,
    offset?: number,
    limit?: number,
    sort?: string,
    search?: string,
    crmType?: string
): URLConstructor =>
    deviceURL(telsysID.toString(), partition.toString(), deviceID)
        .appendPathParams(APIPathParams.CRM_CONFIGURATION, APIPathParams.CONFIGS)
        .appendQueryParams({
            [QueryParams.OFFSET]: offset,
            [QueryParams.LIMIT]: limit,
            [QueryParams.SORT]: sort,
            [QueryParams.SEARCH_QUERY]: search,
            [QueryParams.CRM_TYPE]: crmType === ANY_VALUE_STRING_NUMERIC ? undefined : crmType,
            [QueryParams.CRM_CONFIGURATION_FIELDS]:
                'TelSysDescription,PartitionDescription,DeviceDescription,CMEnvironmentID,TelSysID,DeviceNumber,PartIDInTelSys,ConfigurationType'
        })

export const getCRMTemplate = (crmType: string, templateType: CRMConfigurationTemplateType): URLConstructor =>
    getAPIInternalBaseURL()
        .appendPathParams(APIPathParams.CRM_CONFIGURATION, APIPathParams.CONFIGS, APIPathParams.TEMPLATE, crmType)
        .appendQueryParams({ [QueryParams.CRM_CONFIGURATION_TEMPLATE_TYPE]: templateType })

export const getCRMTypesURL = (
    cmeID?: string,
    telsysID?: string,
    templateType?: CRMConfigurationTemplateType
): URLConstructor =>
    getAPIInternalBaseURL()
        .appendPathParams(APIPathParams.CRM_CONFIGURATION, APIPathParams.TYPES)
        .appendQueryParams({
            [QueryParams.CME_ID]: cmeID,
            [QueryParams.TEL_SYS_ID]: telsysID,
            [QueryParams.CRM_CONFIGURATION_TEMPLATE_TYPE]: templateType
        })

export const getFilterEntityTypesURL: URLConstructor = getAPIInternalBaseURL().appendPathParams(
    APIPathParams.REPORT,
    APIPathParams.FILTER_OPTION,
    APIPathParams.ENTITY_TYPES
)

export const getSecurityGroupsURL = (telSysID: string | number): URLConstructor =>
    getAPIInternalBaseURL().appendPathParams(
        APIPathParams.TEAMS,
        AkixiEntities.TEL_SYS,
        telSysID,
        APIPathParams.SECURITY_GROUPS
    )

export const getUserCompliancePolicyFromDeviceURL = (
    telsys: string | number,
    partition: string | number,
    idOnTelSys: string
): URLConstructor =>
    teamsPartitionBaseURL(telsys, partition).appendPathParams(
        AkixiEntities.DEVICE,
        APIPathParams.ID_ON_TEL_SYS,
        idOnTelSys,
        APIPathParams.POLICIES
    )

export const getUserCompliancePolicyURL = (telsys: string | number, partition: string | number): URLConstructor =>
    teamsPartitionBaseURL(telsys, partition).appendPathParams(APIPathParams.USERS, APIPathParams.POLICIES)

export const getTenantCompliancePoliciesURL = (telsys: string | number): URLConstructor =>
    teamsTelSysBaseURL(telsys).appendPathParams(APIPathParams.COMPLIANCE_RECORDING_POLICIES)

export const getTeamsAsyncResult = (telsys: string | number): URLConstructor =>
    teamsTelSysBaseURL(telsys).appendPathParams(APIPathParams.ASYNC_RESULT)

export const getTeamsBatchAsyncResult = (telsys: string | number): URLConstructor =>
    teamsTelSysBaseURL(telsys).appendPathParams(APIPathParams.BATCH_ASYNC_RESULT)

export const getQueueCompatibilityCheckURL = (
    telsys: string | number,
    partition: string | number,
    deviceNumber: string
): URLConstructor => deviceBaseURL(telsys, partition, deviceNumber).appendPathParams(APIPathParams.INCOMPATIBILITIES)

export const queryPresenceStatusURL = (
    telsys: string | number,
    partition: string | number,
    idOnTelSys: string
): URLConstructor =>
    teamsPartitionBaseURL(telsys, partition).appendPathParams(
        AkixiEntities.DEVICE,
        APIPathParams.ID_ON_TEL_SYS,
        idOnTelSys,
        APIPathParams.INFO
    )

export const getTenantURL = (telsys: string | number): URLConstructor =>
    teamsTelSysBaseURL(telsys).appendPathParams(APIPathParams.TENANT)

export const getTenantUsersURL = (telsys: string | number): URLConstructor =>
    teamsTelSysBaseURL(telsys).appendPathParams(APIPathParams.USERS)

export const getVoiceAppsURL = (telsys: string | number): URLConstructor =>
    teamsTelSysBaseURL(telsys).appendPathParams(APIPathParams.VOICE_APPS)
